import * as React from "react";
import { Stack, Text, Wrap } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { Page, PillLink, Section, SectionHeading } from "components/page";

const Prose = ({ children }: { children: React.ReactNode }) => (
  <Stack spacing={5} className="prose">
    {children}
  </Stack>
);

const Details = () => {
  return (
    <Page title="Hi, I'm Daniel.">
      <Text
        className="prose"
        fontSize={{ base: "xl", md: "2xl" }}
        lineHeight={1.45}
        mb={{ base: 10, md: 14 }}
      >
        I'm Daniel Haşegan (pronounced as daan-e-el hush-eh-gun), a neuroscience PhD candidate
        at NYU studying the brain dynamics of visual perception. I live in New York City but I
        spent my childhood in Târgu Mureș, Romania.
      </Text>

      <Section label="Research">
        <SectionHeading>Spatiotemporal dynamics of visual perception</SectionHeading>
        <Prose>
          <Text>
            I investigate how the brain generates our perception by analyzing the brain dynamics
            that underlie vision. I focus on the temporal coordination of neural activity across
            different brain regions (the spatio temporal dynamics). I am particularly interested in
            understanding how the brain synchronizes information across different regions to create
            a unified perceptual experience.
          </Text>
        </Prose>
      </Section>

      <Section label="Background">
        <SectionHeading>From deep learning to neuroscience</SectionHeading>
        <Prose>
          <Text>
            My path began in computer science, where I earned my degree
            at <strong>Jacobs University Bremen in Germany</strong>. Working with Dr. Michael
            Kohlhase, I developed my bachelor's thesis on integrating mathematical knowledge graphs
            with semantically rich queries—an early exploration of how machines could process
            structured knowledge.
          </Text>
          <Text>
            After graduating in 2014, I dove into deep learning during its explosive growth.
            At <strong>Twitter's Cortex team</strong> in San Francisco, I applied machine learning
            to extract meaning from tweets, images, and videos, directly impacting core products
            including Trends, Ads, Safety, and Recommendations.
          </Text>
          <Text>
            Seeking greater impact on health and society, I moved
            to <a href="https://braid.health">Braid Health</a>, where I worked at the intersection
            of AI and radiology. Collaborating with radiologists, I built classification models that
            improved diagnostic accuracy and efficiency. Yet working in healthcare revealed a crucial
            gap: while deep learning excelled at pattern recognition, it offered limited insight into
            the underlying mechanisms of human disorders. To truly understand complex conditions I was
            helping to diagnose, I needed to study how biological systems work.
          </Text>
          <Text>
            This realization motivated me to study brain processes. I first
            joined <a href="https://www.nki.rfmh.org/lab/dynamical-cognitive-neuroscience-lab/">Sam
            Neymotin's lab</a> at <strong>Nathan Kline Institute for Psychiatric Research</strong>,
            where I developed computational models (Spiking Neural Networks) of brain circuits to
            better understand plasticity rules (Spike-Timing-Dependent Plasticity). Later on, I
            joined the <a href="https://braindynamicslab.github.io/">Brain Dynamics Lab</a> at <strong>Stanford</strong>,
            where I worked with Dr. Manish Saggar using Mapper—a topological data analysis tool—to
            extract dynamic patterns from fMRI data and map the evolving landscape of brain activity.
          </Text>
          <Text>
            In 2022, I began my <a href="https://neuroscience.nyu.edu/">PhD in Neuroscience and
            Physiology at NYU</a>, joining <a href="https://med.nyu.edu/helab/">Dr. Biyu He's lab</a>.
            Now I combine my engineering background with neuroscience methods, using intracranial
            electroencephalography (iEEG) to investigate the neural dynamics underlying visual
            perception. My goal is to bridge computational approaches with mechanistic understanding
            of how the brain processes what we see.
          </Text>
        </Prose>
        <Wrap spacing={3} mt={8}>
          <PillLink to="/publications">Publications</PillLink>
          <PillLink to="/experience">Experience</PillLink>
        </Wrap>
      </Section>

      <Section label="Outside the lab">
        <Prose>
          <Text>
            In my free time, I spend my time creating <RouterLink to="/music">music</RouterLink>.
            Beyond that, my interests encompass snowboarding, rock climbing, immersing myself in
            sci-fi literature, engaging in friendly foosball matches, and gaming.
          </Text>
        </Prose>
      </Section>
    </Page>
  );
};

export default Details;
